import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Percent, IndianRupee, Save } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import toast from 'react-hot-toast';

interface SettingRow { key: string; value: string; description?: string }

const settingsApi = {
  getAll: async (): Promise<SettingRow[]> => {
    const res = await api.get(API_ENDPOINTS.SETTINGS.LIST);
    const raw = (res.data as any)?.data ?? res.data;
    return Array.isArray(raw) ? raw : [];
  },
  update: async (key: string, value: string) => {
    await api.put(API_ENDPOINTS.SETTINGS.UPDATE(key), { value });
  },
};

/**
 * Admin control for the platform fee (flat ₹) and GST % applied on it.
 * Both feed the customer fare estimate. Stored as global system settings.
 */
export function FeesCard() {
  const queryClient = useQueryClient();
  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: settingsApi.getAll,
  });

  const [platformFee, setPlatformFee] = useState('');
  const [gstPercent, setGstPercent] = useState('');

  useEffect(() => {
    const map: Record<string, string> = {};
    settings.forEach((s) => { map[s.key] = s.value; });
    // Platform fee can never be below ₹5 — clamp the displayed value up to the floor.
    const fee = parseFloat(map.platform_fee ?? '5');
    setPlatformFee(String(Number.isFinite(fee) && fee >= 5 ? fee : 5));
    setGstPercent(map.gst_percent ?? '0');
  }, [settings]);

  // Hard floor: the platform fee must be ₹5 or more.
  const feeBelowMin = (parseFloat(platformFee) || 0) < 5;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const fee = Math.max(5, parseFloat(platformFee) || 5);
      const gst = Math.max(0, parseFloat(gstPercent) || 0);
      await settingsApi.update('platform_fee', String(fee));
      await settingsApi.update('gst_percent', String(gst));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success('Platform fee & GST updated');
    },
    onError: () => toast.error('Failed to update fees'),
  });

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4">
          <h3 className="text-base font-semibold">Platform Fee & GST</h3>
          <p className="text-sm text-muted-foreground">
            Applied to every fare and shown to customers on the fare estimate.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6"><Spinner /></div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="platform_fee">Platform Fee (₹)</Label>
              <div className="relative mt-1">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="platform_fee"
                  type="number"
                  min="5"
                  step="0.5"
                  className="pl-9"
                  value={platformFee}
                  onChange={(e) => setPlatformFee(e.target.value)}
                />
              </div>
              {feeBelowMin ? (
                <p className="text-xs text-red-500 mt-1">Platform fee cannot be below ₹5.</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">Minimum ₹5.</p>
              )}
            </div>
            <div className="flex-1">
              <Label htmlFor="gst_percent">GST (%)</Label>
              <div className="relative mt-1">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="gst_percent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  className="pl-9"
                  value={gstPercent}
                  onChange={(e) => setGstPercent(e.target.value)}
                />
              </div>
            </div>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || feeBelowMin}>
              <Save className="h-4 w-4 mr-2" />
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
